<template>
    <div class="book-quick-preview-host">
        <book-quick-preview
            v-if="previewAvailable"
            ref="preview"
            :item="activeItem"
            :context="activeContext"
            :visible="visible"
            :preview-id="previewId"
            :panel-style="panelStyle"
            :busy="busy"
            :error="error"
            :post-completion-prompt="postCompletionPrompt"
            :viewer-surface="surfaceContext === 'PUBLIC_FOREIGN_LIBRARY'"
            :authenticated="authenticated"
            @enter="cancelClose"
            @leave="scheduleClose"
            @focus-enter="cancelClose"
            @focus-leave="handlePanelFocusLeave"
            @return-to-trigger="returnToTrigger"
            @continue-forward="continueForward"
            @layout-change="$nextTick(updatePosition)"
            @lifecycle="runLifecycle"
            @rating-save="saveRating"
            @rating-clear="clearRating"
            @skip-completion-rating="skipCompletionRating"
            @collections="openCollections"
            @manage="$emit('manage', $event)"
            @remove="$emit('remove')"
            @add="openShelfPicker"
        />

        <quick-shelf-picker
            :show="shelfPickerOpen"
            :anchor-element="shelfPickerAnchor"
            :busy="busy"
            @select="selectShelf"
            @close="closeShelfPicker"
        />

        <modal-shell
            ref="collectionsShell"
            :show="collectionModalOpen"
            title-id="quick-preview-collections-title"
            description-id="quick-preview-collections-description"
            modal-class="w-full max-w-lg"
            surface-class="foxy-modal w-full overflow-hidden"
            overlay-class="bg-foxy-cocoa/55 backdrop-blur-sm"
            initial-focus-selector="[data-quick-preview-collections-search]"
            :backdrop-dismissible="!collectionsSaving"
            @backdrop="closeCollections"
            @escape="closeCollections"
        >
            <div class="border-b border-foxy-border px-5 py-4">
                <div class="flex items-start justify-between gap-4">
                    <div>
                        <h2 id="quick-preview-collections-title" class="text-lg font-extrabold text-foxy-text">Добірки</h2>
                        <p id="quick-preview-collections-description" class="mt-1 text-xs text-foxy-text-secondary">
                            Зміни буде застосовано лише після «Зберегти».
                        </p>
                    </div>
                    <button
                        type="button"
                        class="acc-btn min-h-10 !px-3 !py-2"
                        aria-label="Закрити"
                        :disabled="collectionsSaving"
                        @click="closeCollections"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <div class="px-5 py-4">
                <label for="quick-preview-collections-search" class="sr-only">Знайти добірку</label>
                <div class="relative">
                    <i
                        class="fas fa-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foxy-text-secondary"
                        aria-hidden="true"
                    ></i>
                    <input
                        id="quick-preview-collections-search"
                        v-model.trim="collectionSearch"
                        type="search"
                        class="acc-input w-full !pl-10"
                        placeholder="Знайти добірку..."
                        data-quick-preview-collections-search
                    />
                </div>

                <div
                    v-if="membershipConflict"
                    class="mt-4 rounded-xl border border-foxy-honey/45 bg-foxy-honey/10 p-3 text-sm text-foxy-text"
                    role="alert"
                >
                    <p>{{ membershipConflict.message }}</p>
                    <button
                        type="button"
                        class="mt-2 font-extrabold text-foxy-action underline"
                        :disabled="collectionsLoading"
                        @click="recoverCollections"
                    >
                        Оновити дані
                    </button>
                </div>

                <div class="quick-preview-collections-list mt-4" aria-live="polite">
                    <div v-if="collectionsLoading" class="p-4 text-sm text-foxy-text-secondary">Завантажуємо добірки…</div>
                    <div v-else-if="collectionsError" class="p-4 text-sm text-foxy-error">
                        {{ collectionsError }}
                        <button type="button" class="ml-2 font-bold underline" @click="loadCollections()">Спробувати ще</button>
                    </div>
                    <label v-for="library in filteredLibraries" v-else :key="library.id" class="quick-preview-collections-option">
                        <input
                            v-model="selectedLibraryIds"
                            type="checkbox"
                            :value="Number(library.id)"
                            class="h-4 w-4 rounded border-foxy-border text-foxy-action"
                        />
                        <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foxy-text">{{ library.name }}</span>
                        <i v-if="library.is_private" class="fas fa-lock text-xs text-foxy-text-secondary" aria-label="Приватна добірка"></i>
                    </label>
                    <div v-if="!collectionsLoading && !collectionsError && !filteredLibraries.length" class="p-4 text-sm text-foxy-text-secondary">
                        Добірок не знайдено.
                    </div>
                </div>

                <button
                    type="button"
                    class="mt-4 inline-flex min-h-10 items-center gap-2 text-sm font-extrabold text-foxy-action hover:underline"
                    @click="createCollection"
                >
                    <i class="fas fa-plus" aria-hidden="true"></i>Нова добірка
                </button>
            </div>

            <div class="flex justify-end gap-2 border-t border-foxy-border px-5 py-4">
                <button type="button" class="acc-btn" data-modal-cancel :disabled="collectionsSaving" @click="closeCollections">Скасувати</button>
                <button
                    type="button"
                    class="acc-btn-primary"
                    data-modal-primary
                    :disabled="
                        collectionsLoading || collectionsSaving || Boolean(collectionsError) || Boolean(membershipConflict) || !membershipSnapshot
                    "
                    @click="saveCollections"
                >
                    {{ collectionsSaving ? 'Зберігаємо…' : 'Зберегти' }}
                </button>
            </div>
        </modal-shell>
    </div>
</template>

<script>
import axios from 'axios';
import ModalShell from '../ModalShell.vue';
import BookQuickPreview from './BookQuickPreview.vue';
import QuickShelfPicker from '../QuickShelfPicker.vue';
import { normalizeBookCardContext } from '../../utils/bookCardContext';
import { canUseBookQuickPreview } from '../../utils/bookQuickPreviewCapability';
import { lifecycleRequest, shelfChoiceRequest } from '../../utils/quickPreviewActions';

const OPEN_DELAY = 450;
const CLOSE_GRACE = 140;
const VIEWPORT_PADDING = 16;
const GAP = 12;
const COLLECTION_CREATE_POLL_MS = 500;

export default {
    name: 'BookQuickPreviewHost',
    components: { BookQuickPreview, ModalShell, QuickShelfPicker },
    props: {
        items: { type: Array, default: () => [] },
        refreshItems: { type: Function, default: null },
        surfaceContext: { type: String, default: 'OWNER_LIBRARY' },
        authenticated: { type: Boolean, default: false },
    },
    data() {
        return {
            activeItem: null,
            activeContext: null,
            activeStatusId: null,
            activeBookId: null,
            shelfPickerOpen: false,
            shelfPickerAnchor: null,
            trigger: null,
            ignoreFocusTrigger: null,
            skipNextTriggerTab: null,
            visible: false,
            panelStyle: { left: '16px', top: '16px', visibility: 'hidden' },
            openTimer: null,
            closeTimer: null,
            previewId: 'book-quick-preview-owner-library',
            busy: false,
            error: '',
            postCompletionPrompt: false,
            operationGeneration: 0,
            collectionModalOpen: false,
            collectionsLoading: false,
            collectionsSaving: false,
            collectionsError: '',
            membershipConflict: null,
            membershipSnapshot: null,
            libraries: [],
            selectedLibraryIds: [],
            collectionSearch: '',
            collectionLoadGeneration: 0,
            collectionCreateWindow: null,
            collectionCreateTimer: null,
            collectionIdsBeforeCreate: [],
        };
    },
    computed: {
        previewAvailable() {
            return canUseBookQuickPreview(this.authenticated);
        },
        filteredLibraries() {
            const query = this.collectionSearch.toLocaleLowerCase('uk-UA');
            if (!query) return this.libraries;
            return this.libraries.filter((library) =>
                String(library.name || '')
                    .toLocaleLowerCase('uk-UA')
                    .includes(query),
            );
        },
    },
    watch: {
        authenticated() {
            if (!canUseBookQuickPreview(this.authenticated)) this.close();
        },
        items() {
            if (this.visible) this.rebindActiveItem();
        },
    },
    mounted() {
        document.body.appendChild(this.$el);
        document.addEventListener('mousedown', this.handleDocumentPointer, true);
        document.addEventListener('keydown', this.handleDocumentKeydown, true);
        window.addEventListener('resize', this.updatePosition);
        window.addEventListener('scroll', this.updatePosition, true);
    },
    beforeDestroy() {
        this.clearTimers();
        this.stopCollectionCreateMonitor();
        document.removeEventListener('mousedown', this.handleDocumentPointer, true);
        document.removeEventListener('keydown', this.handleDocumentKeydown, true);
        window.removeEventListener('resize', this.updatePosition);
        window.removeEventListener('scroll', this.updatePosition, true);
        if (this.$el?.parentNode) this.$el.parentNode.removeChild(this.$el);
    },
    methods: {
        desktopAvailable() {
            return window.innerWidth >= 768;
        },
        publicInteractionLocked() {
            return (
                this.surfaceContext === 'PUBLIC_FOREIGN_LIBRARY' &&
                this.visible &&
                (this.busy ||
                    this.collectionModalOpen ||
                    this.postCompletionPrompt ||
                    this.$refs.preview?.ratingEditing ||
                    this.$refs.preview?.overflowOpen)
            );
        },
        scheduleOpen(item, trigger, immediate = false) {
            if (!canUseBookQuickPreview(this.authenticated)) return;
            if (immediate && this.ignoreFocusTrigger === trigger) {
                this.ignoreFocusTrigger = null;
                return;
            }
            if (this.shelfPickerOpen || this.publicInteractionLocked()) return;
            if (!this.desktopAvailable() || !item || !trigger) return;
            this.cancelClose();
            if (this.openTimer) clearTimeout(this.openTimer);
            if (immediate) return this.openNow(item, trigger);
            this.openTimer = setTimeout(() => this.openNow(item, trigger), OPEN_DELAY);
        },
        openNow(item, trigger) {
            if (!canUseBookQuickPreview(this.authenticated)) return;
            if (this.shelfPickerOpen || this.publicInteractionLocked()) return;
            if (!trigger?.isConnected) return;
            this.openTimer = null;
            this.operationGeneration += 1;
            this.activeItem = item;
            this.activeContext = normalizeBookCardContext(item);
            this.activeStatusId = this.activeContext.readingStatusId;
            this.activeBookId = item.book?.id ?? null;
            this.trigger = trigger;
            this.skipNextTriggerTab = null;
            this.error = '';
            this.postCompletionPrompt = false;
            this.panelStyle = { left: '16px', top: '16px', visibility: 'hidden' };
            this.visible = true;
            this.$emit('opened', item.id);
            this.$nextTick(this.updatePosition);
        },
        toggle(item, trigger) {
            if (this.isOpenFor(item)) this.close();
            else this.scheduleOpen(item, trigger, true);
        },
        scheduleClose() {
            if (this.collectionModalOpen || this.shelfPickerOpen || this.busy) return;
            if (this.openTimer) {
                clearTimeout(this.openTimer);
                this.openTimer = null;
            }
            if (this.closeTimer) clearTimeout(this.closeTimer);
            this.closeTimer = setTimeout(() => {
                const panel = this.$refs.preview?.$el;
                const active = document.activeElement;
                if (panel?.contains(active) || this.trigger?.contains?.(active)) return;
                this.close({ restoreFocus: false });
            }, CLOSE_GRACE);
        },
        cancelClose() {
            // Entering the panel commits interaction to this Book, including Manage.
            if (this.openTimer) {
                clearTimeout(this.openTimer);
                this.openTimer = null;
            }
            if (!this.closeTimer) return;
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        },
        close({ restoreFocus = false } = {}) {
            this.clearTimers();
            this.stopCollectionCreateMonitor();
            const trigger = this.getTrigger();
            this.operationGeneration += 1;
            this.collectionLoadGeneration += 1;
            this.collectionModalOpen = false;
            this.visible = false;
            this.activeItem = null;
            this.activeContext = null;
            this.activeStatusId = null;
            this.activeBookId = null;
            this.shelfPickerOpen = false;
            this.shelfPickerAnchor = null;
            this.trigger = null;
            this.busy = false;
            this.error = '';
            this.postCompletionPrompt = false;
            this.$emit('closed');
            if (restoreFocus && trigger?.isConnected && !trigger.disabled) {
                this.prepareFocusReturn(trigger);
                this.$nextTick(() => trigger.focus({ preventScroll: true }));
            }
        },
        clearTimers() {
            if (this.openTimer) clearTimeout(this.openTimer);
            if (this.closeTimer) clearTimeout(this.closeTimer);
            this.openTimer = null;
            this.closeTimer = null;
        },
        isOpenFor(item) {
            if (this.surfaceContext === 'PUBLIC_FOREIGN_LIBRARY') return this.visible && Number(this.activeBookId) === Number(item?.book?.id);
            return this.visible && Number(this.activeStatusId) === Number(normalizeBookCardContext(item).readingStatusId);
        },
        getTrigger() {
            if (!this.trigger || this.trigger.tabIndex >= 0) return this.trigger;
            // Pointer entry may originate on the card wrapper rather than a control.
            return this.trigger.querySelector?.('.book-preview-trigger:not(:disabled)') || this.trigger.querySelector?.('a[href]') || this.trigger;
        },
        handleTriggerKeydown(event, item, trigger) {
            if (!canUseBookQuickPreview(this.authenticated)) return;
            if (event.key === 'Tab' && this.skipNextTriggerTab === trigger) {
                this.skipNextTriggerTab = null;
                return;
            }
            if (event.key === 'Escape' && this.isOpenFor(item)) {
                event.preventDefault();
                this.close({ restoreFocus: true });
                return;
            }
            if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
                event.preventDefault();
                this.ignoreFocusTrigger = null;
                this.scheduleOpen(item, trigger, true);
                return;
            }
            if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();
                this.scheduleOpen(item, trigger, true);
                this.$nextTick(() => this.$refs.preview?.focusFirstAction());
            }
        },
        handlePanelFocusLeave(event) {
            if (this.collectionModalOpen || this.shelfPickerOpen) return;
            const next = event.relatedTarget;
            if (this.$refs.preview?.$el?.contains(next) || this.trigger?.contains?.(next)) return;
            this.scheduleClose();
        },
        handleDocumentPointer(event) {
            if (!this.visible || this.collectionModalOpen || this.shelfPickerOpen) return;
            if (this.$refs.preview?.$el?.contains(event.target) || this.trigger?.contains?.(event.target)) return;
            this.close({ restoreFocus: false });
        },
        handleDocumentKeydown(event) {
            if (event.key !== 'Escape' || !this.visible || this.collectionModalOpen || this.shelfPickerOpen) return;
            event.preventDefault();
            if (this.$refs.preview?.overflowOpen) {
                this.$refs.preview.closeOverflowMenu({ restoreFocus: true });
                return;
            }
            this.close({ restoreFocus: true });
        },
        returnToTrigger() {
            this.close({ restoreFocus: true });
        },
        prepareFocusReturn(trigger) {
            this.ignoreFocusTrigger = trigger;
            this.skipNextTriggerTab = trigger;
        },
        continueForward() {
            const trigger = this.getTrigger();
            const controls = Array.from(
                document.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]'),
            ).filter(
                (element) =>
                    !this.$el.contains(element) &&
                    !element.disabled &&
                    element.tabIndex >= 0 &&
                    element.getClientRects().length &&
                    !element.closest('[inert], [hidden]'),
            );
            const index = controls.indexOf(trigger);
            const next = index >= 0 ? controls[index + 1] : null;
            this.close({ restoreFocus: false });
            if (next) this.$nextTick(() => next.focus());
            else if (trigger?.isConnected) {
                this.prepareFocusReturn(trigger);
                trigger.focus({ preventScroll: true });
            }
        },
        async runLifecycle(action) {
            if (this.busy || !this.activeItem || !this.activeContext) return;
            if (this.authenticated === false || (this.surfaceContext === 'PUBLIC_FOREIGN_LIBRARY' && !this.activeContext.owned)) return;
            const generation = this.operationGeneration;
            const completingReadingId = action === 'complete' ? this.activeContext.actionReading?.readingInstanceId : null;
            this.busy = true;
            this.error = '';
            try {
                const request = lifecycleRequest(action, this.activeItem, this.activeContext);
                await axios[request.method](request.url, request.data);
                if (generation !== this.operationGeneration) return;
                const rebound = await this.refreshAndRebind(generation);
                if (!rebound) return;
                const completed = this.activeContext?.actionReading;
                this.postCompletionPrompt =
                    action === 'complete' &&
                    completed?.state === 'completed' &&
                    Number(completed.readingInstanceId) === Number(completingReadingId) &&
                    completed.rating === null;
                this.$nextTick(this.updatePosition);
            } catch (requestError) {
                if (generation !== this.operationGeneration) return;
                if (requestError?.response?.status === 409) {
                    await this.refreshAndRebind(generation);
                    this.error = requestError.response.data?.message || 'Дані читання змінилися. Перегляд оновлено.';
                } else {
                    this.error = requestError?.response?.data?.message || 'Не вдалося змінити стан читання.';
                }
            } finally {
                if (generation === this.operationGeneration) this.busy = false;
            }
        },
        async saveRating(rating) {
            return this.mutateRating(rating);
        },
        async clearRating() {
            return this.mutateRating(null);
        },
        async mutateRating(rating) {
            const reading = this.activeContext?.actionReading;
            const statusId = this.activeContext?.readingStatusId;
            if (this.busy || !reading?.readingInstanceId || !statusId) return;
            if (this.authenticated === false) return;
            const generation = this.operationGeneration;
            this.busy = true;
            this.error = '';
            try {
                await axios.patch(`/api/account/reading-status/${statusId}/history/${reading.readingInstanceId}`, {
                    rating,
                    expected_reading_status_lock_version: this.activeContext.readingStatusLockVersion,
                    expected_reading_instance_lock_version: reading.lockVersion,
                });
                if (generation !== this.operationGeneration) return;
                await this.refreshAndRebind(generation);
                this.postCompletionPrompt = false;
                this.$refs.preview?.cancelRatingEditor();
            } catch (requestError) {
                if (generation !== this.operationGeneration) return;
                if (requestError?.response?.status === 409) await this.refreshAndRebind(generation);
                this.error =
                    requestError?.response?.data?.errors?.rating?.[0] || requestError?.response?.data?.message || 'Не вдалося зберегти оцінку.';
            } finally {
                if (generation === this.operationGeneration) this.busy = false;
            }
        },
        skipCompletionRating() {
            this.postCompletionPrompt = false;
            this.$nextTick(this.updatePosition);
        },
        async refreshAndRebind(generation = this.operationGeneration) {
            if (this.refreshItems) await this.refreshItems({ ownerFailureMessage: 'Зміни збережено, але не вдалося оновити бібліотеку.' });
            if (generation !== this.operationGeneration || !this.visible) return false;
            await this.$nextTick();
            return this.rebindActiveItem();
        },
        rebindActiveItem() {
            if (!this.visible) return false;
            const current =
                this.surfaceContext === 'PUBLIC_FOREIGN_LIBRARY'
                    ? this.items.find((item) => Number(item.book?.id) === Number(this.activeBookId))
                    : this.activeStatusId === null
                      ? null
                      : this.items.find((item) => Number(normalizeBookCardContext(item).readingStatusId) === Number(this.activeStatusId));
            if (!current) {
                this.close({ restoreFocus: false });
                return false;
            }
            this.activeItem = current;
            this.activeContext = normalizeBookCardContext(current);
            this.activeStatusId = this.activeContext.readingStatusId;
            this.$nextTick(this.updatePosition);
            return true;
        },
        async openCollections() {
            if (!this.activeItem?.book?.slug) return;
            if (this.authenticated === false || (this.surfaceContext === 'PUBLIC_FOREIGN_LIBRARY' && !this.activeContext?.owned)) return;
            this.cancelClose();
            this.collectionModalOpen = true;
            this.collectionSearch = '';
            this.collectionsError = '';
            this.membershipConflict = null;
            this.$refs.collectionsShell?.setReturnFocusTarget(document.activeElement);
            await this.loadCollections();
        },
        openShelfPicker(anchor) {
            if (this.authenticated === false || this.busy || this.activeContext?.owned || !this.visible) return;
            this.cancelClose();
            this.shelfPickerAnchor = anchor;
            this.shelfPickerOpen = true;
        },
        closeShelfPicker({ restoreFocus = false } = {}) {
            const anchor = this.shelfPickerAnchor;
            this.shelfPickerOpen = false;
            this.shelfPickerAnchor = null;
            if (restoreFocus) this.$nextTick(() => anchor?.focus());
        },
        async selectShelf(status) {
            if (this.authenticated === false || this.busy || !this.shelfPickerOpen || !this.activeItem) return;
            const generation = this.operationGeneration;
            this.busy = true;
            this.error = '';
            try {
                const request = shelfChoiceRequest(status, this.activeItem, this.activeContext);
                await axios[request.method](request.url, request.data);
                if (generation !== this.operationGeneration) return;
                this.closeShelfPicker();
                await this.refreshAndRebind(generation);
                this.$nextTick(() => this.$refs.preview?.focusFirstAction());
            } catch (requestError) {
                if (generation !== this.operationGeneration) return;
                this.closeShelfPicker({ restoreFocus: true });
                if (requestError?.response?.status === 409) await this.refreshAndRebind(generation);
                this.error = requestError?.response?.data?.message || 'Не вдалося додати книгу до бібліотеки.';
            } finally {
                if (generation === this.operationGeneration) this.busy = false;
            }
        },
        async loadCollections({ preserveSelection = false, forceCatalog = false } = {}) {
            const slug = this.activeItem?.book?.slug;
            if (!slug) return;
            const intended = preserveSelection ? [...this.selectedLibraryIds] : [];
            const generation = ++this.collectionLoadGeneration;
            this.collectionsLoading = true;
            this.collectionsError = '';
            try {
                const [{ data: catalog }, { data: memberships }] = await Promise.all([
                    axios.get('/api/account/libraries', { params: forceCatalog ? { _: Date.now() } : {} }),
                    axios.get(`/api/books/${slug}/libraries`, { params: forceCatalog ? { _: Date.now() } : {} }),
                ]);
                if (generation !== this.collectionLoadGeneration || !this.collectionModalOpen) return;
                if (
                    !Array.isArray(catalog?.libraries) ||
                    !Array.isArray(memberships?.library_ids) ||
                    typeof memberships?.membership_snapshot !== 'string'
                ) {
                    throw new Error('Canonical collection state is missing.');
                }
                this.libraries = catalog.libraries;
                this.membershipSnapshot = memberships.membership_snapshot;
                this.selectedLibraryIds = preserveSelection ? intended : memberships.library_ids.map(Number);
                this.membershipConflict = null;
            } catch {
                if (generation !== this.collectionLoadGeneration || !this.collectionModalOpen) return;
                this.collectionsError = 'Не вдалося завантажити добірки.';
            } finally {
                if (generation === this.collectionLoadGeneration) this.collectionsLoading = false;
            }
        },
        async recoverCollections() {
            await this.loadCollections({ preserveSelection: true, forceCatalog: true });
        },
        closeCollections() {
            if (this.collectionsSaving) return;
            this.collectionModalOpen = false;
            this.collectionLoadGeneration += 1;
            this.collectionsLoading = false;
            this.collectionsError = '';
            this.membershipConflict = null;
            this.membershipSnapshot = null;
            this.stopCollectionCreateMonitor();
        },
        async saveCollections() {
            if (this.collectionsSaving || this.membershipConflict || !this.membershipSnapshot || !this.activeContext) return;
            const generation = this.operationGeneration;
            this.collectionsSaving = true;
            this.collectionsError = '';
            try {
                await axios.put(`/api/account/reading-status/${this.activeContext.readingStatusId}`, {
                    status: this.activeContext.shelfStatus,
                    lock_version: this.activeContext.readingStatusLockVersion,
                    library_ids: [...this.selectedLibraryIds].map(Number),
                    membership_snapshot: this.membershipSnapshot,
                });
                if (generation !== this.operationGeneration) return;
                this.closeCollectionsAfterSave();
                await this.refreshAndRebind(generation);
            } catch (requestError) {
                if (generation !== this.operationGeneration) return;
                if (requestError?.response?.status === 409 && requestError?.response?.data?.code === 'STALE_COLLECTION_MEMBERSHIPS') {
                    this.membershipSnapshot = null;
                    this.membershipConflict = {
                        message: requestError.response.data.message || 'Добірки змінилися в іншому вікні. Оновіть дані перед повторним збереженням.',
                        current: requestError.response.data.current || null,
                    };
                } else {
                    this.collectionsError = requestError?.response?.data?.message || 'Не вдалося зберегти добірки.';
                }
            } finally {
                if (generation === this.operationGeneration) this.collectionsSaving = false;
            }
        },
        closeCollectionsAfterSave() {
            this.collectionModalOpen = false;
            this.collectionLoadGeneration += 1;
            this.membershipSnapshot = null;
            this.membershipConflict = null;
        },
        createCollection() {
            if (this.collectionCreateWindow && !this.collectionCreateWindow.closed) {
                this.collectionCreateWindow.focus();
                return;
            }
            this.collectionIdsBeforeCreate = this.libraries.map((library) => Number(library.id));
            this.collectionCreateWindow = window.open('/libraries/create', 'foxy-quick-preview-create-collection', 'popup,width=760,height=780');
            if (!this.collectionCreateWindow) {
                this.collectionsError = 'Дозвольте спливне вікно, щоб створити добірку.';
                return;
            }
            this.monitorCollectionCreate();
        },
        monitorCollectionCreate() {
            this.stopCollectionCreateMonitor(false);
            this.collectionCreateTimer = setInterval(async () => {
                const popup = this.collectionCreateWindow;
                if (!popup || popup.closed) {
                    this.stopCollectionCreateMonitor();
                    return;
                }
                let completed = false;
                try {
                    completed = /^\/users\/[^/]+\/libraries\/[^/]+\/?$/.test(popup.location.pathname);
                } catch {
                    return;
                }
                if (!completed) return;
                popup.close();
                this.stopCollectionCreateMonitor();
                const before = new Set(this.collectionIdsBeforeCreate);
                const intended = new Set(this.selectedLibraryIds.map(Number));
                await this.loadCollections({ preserveSelection: true, forceCatalog: true });
                this.libraries.forEach((library) => {
                    if (!before.has(Number(library.id))) intended.add(Number(library.id));
                });
                this.selectedLibraryIds = [...intended];
            }, COLLECTION_CREATE_POLL_MS);
        },
        stopCollectionCreateMonitor(clearWindow = true) {
            if (this.collectionCreateTimer) clearInterval(this.collectionCreateTimer);
            this.collectionCreateTimer = null;
            if (clearWindow) this.collectionCreateWindow = null;
        },
        updatePosition() {
            if (!this.visible || !this.trigger?.isConnected) return;
            const triggerRect = this.trigger.getBoundingClientRect();
            const panel = this.$refs.preview?.$el;
            const width = panel?.offsetWidth || Math.min(368, window.innerWidth - VIEWPORT_PADDING * 2);
            const height = panel?.offsetHeight || 360;
            let left = triggerRect.right + GAP;
            if (left + width > window.innerWidth - VIEWPORT_PADDING) left = triggerRect.left - width - GAP;
            left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - width - VIEWPORT_PADDING));
            let top = triggerRect.top;
            if (top + height > window.innerHeight - VIEWPORT_PADDING) top = triggerRect.bottom - height;
            top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - height - VIEWPORT_PADDING));
            this.panelStyle = { left: `${Math.round(left)}px`, top: `${Math.round(top)}px`, visibility: 'visible' };
        },
    },
};
</script>

<style scoped>
.book-quick-preview-host {
    position: static;
}
.quick-preview-collections-list {
    max-height: min(19rem, 42vh);
    overflow-y: auto;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.85rem;
    background: rgb(var(--foxy-surface) / 0.72);
}
.quick-preview-collections-option {
    display: flex;
    min-height: 3rem;
    cursor: pointer;
    align-items: center;
    gap: 0.75rem;
    padding: 0.7rem 0.85rem;
}
.quick-preview-collections-option + .quick-preview-collections-option {
    border-top: 1px solid rgb(var(--foxy-border));
}
.quick-preview-collections-option:hover {
    background: rgb(var(--foxy-elevated));
}
</style>
